<template>
  <div class="generic-label" :class="labelClasses">
    <SvgIcon v-if="showIcon" class="icon" name="sparkle" />
    <span v-if="!iconOnly" class="nowrap">{{ props.text }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SvgIcon from '@/components/common/icons/SvgIcon.vue'
import { GenericLabelColor, GenericLabelSize } from '@/components/common/label/generic-label/utils'

interface GenericLabelProps {
  text?: string
  color?: GenericLabelColor
  size?: GenericLabelSize
  iconOnly?: boolean
  showIcon?: boolean
}

const props = withDefaults(defineProps<GenericLabelProps>(), {
  color: GenericLabelColor.Primary,
  size: GenericLabelSize.Large,
  iconOnly: false,
  showIcon: false
})

const labelClasses = computed(() => [
  props.color,
  props.size,
  {
    'icon-only': props.iconOnly,
    'with-icon': props.showIcon && !props.iconOnly
  }
])
</script>

<style scoped lang="scss">
.generic-label {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  font-family: 'FT Sterling', sans-serif;
  font-weight: 400;
  border-radius: 4px;
  gap: 4px;
  white-space: nowrap;

  // --- Sizes ---

  &.large {
    height: 1.75rem;
    font-size: 0.875rem;
    padding: 4px 8px;
    
    &.icon-only {
      width: 1.75rem;
      padding: 0;
    }

    :deep(.icon) {
      height: 1rem;
      width: 1rem;
    }
  }

  &.medium {
    height: 1.5rem;
    font-size: 0.75rem;
    padding: 4px 6px;

    &.icon-only {
      width: 1.5rem;
      padding: 0;
    }

    :deep(.icon) {
      height: 1rem;
      width: 1rem;
    }
  }

  &.small {
    height: 1.5rem;
    font-size: 0.75rem;
    padding: 2px 4px;

    &.icon-only {
      width: 1.25rem;
      padding: 0;
    }

    :deep(.icon) {
      height: 0.75rem;
      width: 0.75rem;
    }
  }

  // --- Colors ---

  &.primary {
    background: $rebranding-primary-50;
    color: $rebranding-primary-500;
  }

  &.secondary {
    background: $rebranding-secondary-100;
    color: $rebranding-secondary-500;
  }

  &.error {
    background: $rebranding-error-100;
    color: $rebranding-error-500;
  }

  &.success {
    background: $rebranding-success-100;
    color: $rebranding-success-700;
  }

  &.warning {
    background: $rebranding-warning-100;
    color: $rebranding-warning-700;
  }

  &.gray {
    background: $rebranding-grey-300;
    color: $rebranding-grey-900;
  }

  &.white {
    background: $white;
    color: $rebranding-grey-900;
    border: 1px solid $rebranding-grey-400;
  }

  &.black {
    background: $rebranding-grey-900;
    color: $white;
  }

  &.turquoise {
    background: $rebranding-turquoise-100;
    color: $rebranding-turquoise-600;
  }
}
</style>
